import { ReactNode, useMemo, useState } from 'react';
import { Controller, DefaultValues, type SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useLingui } from '@lingui/react';
import { VestingFrequency, VestingKind } from '@startuptools/common/stock-options';
import { useFieldSync } from '@startuptools/common/react-hooks';
import { useOptionsLeft } from './hooks';
import { getDurationSelectOptions } from '../../../../../../../helpers/wpp-duration';
import { BigNumber } from 'bignumber.js';
import { claimRecalc } from '../../../../../../../common/claim-recalc';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { FormLabel } from '@startuptools/ui/FormLabel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Input } from '@startuptools/ui/Input';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { Select } from '@startuptools/ui/Select';
import { Option } from '@startuptools/ui/Option';
import { Checkbox } from '@startuptools/ui/Checkbox';
import { Stack } from '@startuptools/ui/Stack';
import { FormControl } from '@startuptools/ui/FormControl';
import { Typography } from '@startuptools/ui/Typography';
import { Divider } from '@startuptools/ui/Divider';
import { FormHelperText } from '@startuptools/ui/FormHelperText';
import { PaymentKind } from '@startuptools/common/participants';
import { GqlWarrantProgramKind } from '../../../../../../../graphql/react-operations';
import { DateTime } from 'luxon';
import { isNil } from 'lodash-es';
import { type EditWarrantParticipant } from './types';
import { GqlWarrantProgram, type GqlCompany } from '../../../../../../../graphql/base-types.graphql';
import { Switch } from '@startuptools/ui/Switch';
import { paymentKindHumanString } from '../../../../../../../components/payment-kind-human/PaymentKindHuman';
interface FormInputs {
  paymentMethod: PaymentKind;
  shareClass: string;
  quantity: string;
  vestingKind: VestingKind;
  vestingFrequency: VestingFrequency;
  vestingDuration: number;
  vestingCliffDuration: number;
  customVesting: boolean;
  vestingAcceleration: boolean;
  repurchaseVested: boolean;
  email: string;
  phone: string;
  forcedRedemption: boolean;
  claim: string;
  leaveOfAbsence: boolean;
  leaveOfAbsenceDays: number;
}

const calcNumberOfOptionsByClaim = (claim: string, pricePerWarrant: BigNumber, sharesLeftToDistribute: number) => {
  const { newClaim, newUnits } = claimRecalc(new BigNumber(claim), pricePerWarrant, sharesLeftToDistribute);
  return { newClaim: newClaim.toString(), newUnits: newUnits.toString() };
};

const calcClaimByNumberOfOptions = (quantity: string, pricePerWarrant: BigNumber) => {
  const claim = pricePerWarrant.times(quantity);
  return { newClaim: claim.toString() };
};

export const OptionsSubscriberForm = ({
  company,
  enableEditQuantity = true,
  error,
  closeDialog,
  onSubmit,
  participant,
  warrantProgram,
  paymentKind,
  toggleCustomVestingForm,
  infoBanner,
  defaultValues,
}: {
  infoBanner?: ReactNode | undefined;
  company: Pick<GqlCompany, 'id' | 'allowShortQeso' | 'organizationNumber'>;
  enableEditQuantity?: boolean;
  error?: ReactNode | undefined;
  closeDialog: () => void;
  onSubmit: SubmitHandler<FormInputs>;
  participant?: EditWarrantParticipant;
  warrantProgram: Pick<
    GqlWarrantProgram,
    | 'warrantStartDate'
    | 'warrantEndDate'
    | 'warrantKind'
    | 'pricePerWarrant'
    | 'usesStepper'
    | 'shareClasses'
    | 'pricePerWarrant'
    | 'id'
  >;
  paymentKind: PaymentKind;
  toggleCustomVestingForm: () => void;
  defaultValues: DefaultValues<FormInputs>;
}) => {
  const [showAdvancedFields, setShowAdvancedFields] = useState(Boolean(participant));

  const form = useForm<FormInputs>({
    defaultValues,
  });

  const { register, handleSubmit, watch, formState, setValue } = form;

  const [currentShareClass, currentVestingKind, currentVestingFrequency, _currentVestingDuration, leaveOfAbsence] =
    watch(['shareClass', 'vestingKind', 'vestingFrequency', 'vestingDuration', 'leaveOfAbsence']);

  const sharesLeftToDistribute =
    useOptionsLeft(
      company.id,
      warrantProgram.id,
      currentShareClass ?? participant?.shareClass.name ?? 'STAM',
      participant,
    ) ?? 0;

  const durationSelectionOptions = useMemo(() => {
    if (isNil(warrantProgram.warrantStartDate) || isNil(warrantProgram.warrantEndDate)) {
      return null;
    }
    return getDurationSelectOptions(
      DateTime.fromISO(warrantProgram.warrantStartDate),
      DateTime.fromISO(warrantProgram.warrantEndDate),
      currentVestingFrequency,
      warrantProgram.warrantKind === GqlWarrantProgramKind.Employee && !company?.allowShortQeso ? 36 : 0,
    );
  }, [company?.allowShortQeso, warrantProgram, currentVestingFrequency]);

  useFieldSync({
    form,
    fields: {
      claim: claim => {
        const { newUnits } = calcNumberOfOptionsByClaim(
          claim,
          new BigNumber(warrantProgram.pricePerWarrant ?? '0'),
          sharesLeftToDistribute,
        );
        if (newUnits === '0') {
          return {};
        }
        return { quantity: newUnits };
      },
      quantity: quantity => {
        const { newClaim } = calcClaimByNumberOfOptions(quantity, new BigNumber(warrantProgram.pricePerWarrant ?? '0'));
        return { claim: newClaim };
      },
    },
    enabled:
      (participant?.paymentKind ?? paymentKind) === PaymentKind.Offset && warrantProgram.pricePerWarrant !== null,
  });

  const programHasVesting =
    warrantProgram.warrantKind === GqlWarrantProgramKind.Warrant ||
    warrantProgram.warrantKind === GqlWarrantProgramKind.Employee;

  const participantHasDetailedVestingSettings =
    programHasVesting && currentVestingKind !== VestingKind.None && currentVestingKind !== VestingKind.Custom;

  const { i18n } = useLingui();

  return (
    <form
      onSubmit={handleSubmit(async data => {
        await onSubmit(data);
        closeDialog();
      })}
    >
      <Box sx={{ width: '420px', overflowY: 'auto' }}>
        <Stack spacing={3}>
          {infoBanner}
          <FormControl>
            <Stack direction="row" gap={2} alignItems="center">
              <Switch checked={showAdvancedFields} onChange={event => setShowAdvancedFields(event.target.checked)} />
              <FormLabel>
                <Trans id="Advanced settings" />
              </FormLabel>
            </Stack>
          </FormControl>
          <Controller
            name="paymentMethod"
            control={form.control}
            render={({ field }) => (
              <FormControl>
                <FormLabel>
                  <Trans id="Payment method" />
                </FormLabel>
                <Input value={paymentKindHumanString(i18n)[field.value]} disabled />
              </FormControl>
            )}
          />
          {warrantProgram.warrantKind !== GqlWarrantProgramKind.Wise && warrantProgram.usesStepper && (
            <FormControl>
              <FormLabel>
                <Trans id="Share class" />
              </FormLabel>
              <Controller
                name="shareClass"
                control={form.control}
                render={({ field }) => (
                  <Select {...field} onChange={(_event, value) => field.onChange(value)}>
                    {warrantProgram.shareClasses.map(sc => (
                      <Option value={sc.name} key={sc.name}>
                        <Trans
                          id="{shareClass} (vote weight: {voteWeight})"
                          values={{ shareClass: sc.name, voteWeight: sc.voteWeight }}
                        />
                      </Option>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          )}
          {enableEditQuantity && (
            <>
              <Controller
                control={form.control}
                name="quantity"
                rules={{ required: true, min: 1, max: sharesLeftToDistribute }}
                render={({ field: { onChange, value, ...field }, formState }) => (
                  <FormControl error={Boolean(formState.errors.quantity)}>
                    <FormLabel>
                      <Trans id="Quantity" />
                    </FormLabel>
                    <NumericFormatInput
                      {...field}
                      value={value}
                      decimalScale={0}
                      onChange={e => {
                        if (e.target.value !== value) {
                          onChange(e);
                        }
                      }}
                      lang={i18n.locale}
                      endDecorator={
                        <Button
                          variant="soft"
                          color="neutral"
                          onClick={() => {
                            setValue('quantity', sharesLeftToDistribute.toString());
                          }}
                        >
                          <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                            {sharesLeftToDistribute}
                          </NumberFormat>
                        </Button>
                      }
                    />
                    {Boolean(formState.errors.quantity) && (
                      <FormHelperText color="danger">
                        <InfoOutlinedIcon />
                        {formState.errors.quantity?.type === 'max' && (
                          <Trans
                            id="There's only {quantity} shares left to allocate"
                            values={{
                              quantity: (
                                <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                                  {sharesLeftToDistribute}
                                </NumberFormat>
                              ),
                            }}
                          />
                        )}
                        {formState.errors.quantity?.type === 'min' && <Trans id="Allocate at least 1 share" />}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
              {(participant?.paymentKind ?? paymentKind) === PaymentKind.Offset && (
                <Controller
                  control={form.control}
                  name="claim"
                  render={({ field: { onChange, value, ...field } }) => (
                    <FormControl>
                      <FormLabel>
                        <Trans id="Claim" />
                      </FormLabel>
                      <NumericFormatInput
                        {...field}
                        value={value}
                        onChange={e => {
                          if (e.target.value !== value) {
                            onChange(e);
                          }
                        }}
                        onBlur={() => {
                          const { newClaim } = calcNumberOfOptionsByClaim(
                            value ?? '0',
                            new BigNumber(warrantProgram.pricePerWarrant!),
                            sharesLeftToDistribute,
                          );
                          setValue('claim', newClaim, { shouldValidate: true });
                        }}
                        lang={i18n.locale}
                        endDecorator={
                          <>
                            <Divider orientation="vertical" />
                            <Typography ml={2}>
                              <Trans id="SEK" />
                            </Typography>
                          </>
                        }
                      />
                      {value && (
                        <FormHelperText>
                          <Trans
                            id="Claim will be {claim}"
                            values={{
                              claim: (
                                <NumberFormat locale={i18n.locale} variant="currency" currency="SEK">
                                  {value}
                                </NumberFormat>
                              ),
                            }}
                          />
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              )}
            </>
          )}

          {showAdvancedFields && warrantProgram.warrantKind === GqlWarrantProgramKind.Warrant && (
            <FormControl>
              <Controller
                name="forcedRedemption"
                control={form.control}
                render={({ field: { value, ...props } }) => (
                  <Checkbox
                    {...props}
                    checked={value}
                    onChange={e => props.onChange(e.target.checked)}
                    label={<Trans id="Forced redemption" />}
                  />
                )}
              ></Controller>
            </FormControl>
          )}

          {showAdvancedFields && (
            <>
              {programHasVesting && company.organizationNumber !== '5590606579' && (
                <FormControl>
                  <FormLabel>
                    <Trans id="Vesting coupled to" />
                  </FormLabel>
                  <Controller
                    name="vestingKind"
                    control={form.control}
                    render={({ field: props }) => (
                      <Select {...props} onChange={(_event, value) => props.onChange(value)}>
                        {warrantProgram.warrantKind === GqlWarrantProgramKind.Warrant && (
                          <Option value={VestingKind.None}>
                            <Trans id="No vesting" />
                          </Option>
                        )}
                        <Option value={VestingKind.Employment}>
                          <Trans id="Employment" />
                        </Option>
                        <Option value={VestingKind.BoardMembership}>
                          <Trans id="Board membership" />
                        </Option>
                        {warrantProgram.warrantKind === GqlWarrantProgramKind.Warrant && (
                          <>
                            <Option value={VestingKind.AdvisoryBoard}>
                              <Trans id="Advisory board membership" />
                            </Option>
                            <Option value={VestingKind.Custom}>
                              <Trans id="Other vesting" />
                            </Option>
                          </>
                        )}
                      </Select>
                    )}
                  />
                </FormControl>
              )}

              {currentVestingKind === VestingKind.Custom && (
                <Box>
                  <Button
                    onClick={async () => {
                      /* save changes in the form, otherwise theyre lost. */
                      await handleSubmit(onSubmit)();
                      toggleCustomVestingForm();
                    }}
                  >
                    <Trans id="Configure custom vesting" />
                  </Button>
                </Box>
              )}

              {participantHasDetailedVestingSettings && (
                <>
                  <Controller
                    name="vestingFrequency"
                    control={form.control}
                    render={({ field: props, formState }) => (
                      <FormControl error={Boolean(formState.errors.vestingFrequency)}>
                        <FormLabel>
                          <Trans id="Vesting frequency" />
                        </FormLabel>
                        <Select {...props} onChange={(_event, value) => props.onChange(value)}>
                          <Option value={VestingFrequency.Monthly}>
                            <Trans id="Month" />
                          </Option>
                          <Option value={VestingFrequency.Quarterly}>
                            <Trans id="Quarter" />
                          </Option>
                          <Option value={VestingFrequency.Biyearly}>
                            <Trans id="Half a year" />
                          </Option>
                          <Option value={VestingFrequency.Yearly}>
                            <Trans id="Year" />
                          </Option>
                        </Select>
                      </FormControl>
                    )}
                  />

                  <Controller
                    name="vestingDuration"
                    control={form.control}
                    render={({ field: props, formState }) => (
                      <FormControl error={Boolean(formState.errors.vestingDuration)}>
                        <FormLabel>
                          <Trans id="Vesting duration" />
                        </FormLabel>
                        <Select {...props} onChange={(_event, value) => props.onChange(value)}>
                          {durationSelectionOptions?.vestingDuration.map(cd => (
                            <Option key={cd.value} value={cd.value}>
                              {cd.text}
                            </Option>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />

                  <Controller
                    name="vestingCliffDuration"
                    control={form.control}
                    render={({ field: props, formState }) => (
                      <FormControl error={Boolean(formState.errors.vestingCliffDuration)}>
                        <FormLabel>
                          <Trans id="Cliff" />
                        </FormLabel>
                        <Select
                          {...props}
                          onChange={(_event, value) => props.onChange(value)}
                          color={formState.errors.vestingCliffDuration ? 'danger' : undefined}
                        >
                          {warrantProgram.warrantKind !== GqlWarrantProgramKind.Employee && (
                            <Option value={0}>
                              <Trans id="No cliff" />
                            </Option>
                          )}
                          {durationSelectionOptions?.cliffDuration.map(cd => (
                            <Option key={cd.value} value={cd.value}>
                              {cd.text}
                            </Option>
                          ))}
                        </Select>
                        {formState.errors.vestingCliffDuration?.type === 'max' && (
                          <Typography color="danger" fontSize="sm">
                            <Trans id="Can not be after vesting" />
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />
                </>
              )}
              {[GqlWarrantProgramKind.Employee, GqlWarrantProgramKind.Warrant].includes(warrantProgram.warrantKind) &&
                currentVestingKind !== VestingKind.Custom &&
                currentVestingKind !== VestingKind.None && (
                  <>
                    <Controller
                      control={form.control}
                      name="leaveOfAbsence"
                      render={({ field: { value, onChange, ...field } }) => (
                        <Checkbox
                          {...field}
                          checked={value}
                          onChange={() => onChange(!value)}
                          label={<Trans id="Regulate leave of absence" />}
                        />
                      )}
                    />
                    {leaveOfAbsence}
                    {leaveOfAbsence && (
                      <FormControl>
                        <FormLabel>
                          <Trans id="Leave of absence" />
                        </FormLabel>

                        {formState.errors.leaveOfAbsenceDays && (
                          <Typography fontSize="sm" color="danger">
                            <Trans id="Must be between 1 and 365 days" />
                          </Typography>
                        )}

                        <Typography fontSize="sm">
                          <Trans id="Uninterrupted absence after which vesting is extended with an equivalent amount of days." />
                        </Typography>

                        <Input
                          type="number"
                          defaultValue={participant?.leaveOfAbsenceDays}
                          {...register('leaveOfAbsenceDays', { valueAsNumber: true, min: 0, max: 365 })}
                          error={Boolean(formState.errors.leaveOfAbsenceDays)}
                          endDecorator={
                            <>
                              <Divider orientation="vertical" />
                              <Typography ml={2}>
                                <Trans id="Days" />
                              </Typography>
                            </>
                          }
                        />
                      </FormControl>
                    )}
                  </>
                )}
              {participantHasDetailedVestingSettings && (
                <FormControl>
                  <Controller
                    name="vestingAcceleration"
                    control={form.control}
                    render={({ field: { value, ...props } }) => (
                      <Checkbox
                        {...props}
                        checked={value}
                        onChange={e => props.onChange(e.target.checked)}
                        label={<Trans id="Accelerated vesting" />}
                      />
                    )}
                  ></Controller>
                </FormControl>
              )}
              {participantHasDetailedVestingSettings &&
                warrantProgram.warrantKind === GqlWarrantProgramKind.Warrant && (
                  <FormControl>
                    <Controller
                      name="repurchaseVested"
                      control={form.control}
                      render={({ field: { value, ...props } }) => (
                        <Checkbox
                          {...props}
                          checked={value}
                          onChange={e => props.onChange(e.target.checked)}
                          label={<Trans id="Repurchase of vested options" />}
                        />
                      )}
                    ></Controller>
                  </FormControl>
                )}
            </>
          )}

          <>
            <FormControl>
              <FormLabel>
                <Trans id="Email" />
              </FormLabel>
              <Input {...register('email')} />
            </FormControl>
            <FormControl>
              <FormLabel>
                <Trans id="Phone" />
              </FormLabel>
              <Input {...register('phone')} />
            </FormControl>
          </>

          {error}
          <Stack direction="row" gap={1} justifyContent="end" alignItems="center">
            <Button variant="plain" onClick={closeDialog} color="neutral">
              <Trans id="Cancel" />
            </Button>
            <Button type="submit" variant="solid" color="primary">
              <Trans id="Save" />
            </Button>
          </Stack>
        </Stack>
      </Box>
    </form>
  );
};
